/**
 * Prompts for the text-improvement capability.
 *
 * Provider-neutral on purpose: any adapter (OpenAI, Gemini, Claude, Ollama)
 * must be able to reuse these verbatim. Nothing here may mention a vendor,
 * a model id, or an HTTP concern.
 */

export type ImproveField = 'title' | 'description';

export interface ImprovePromptInput {
  field: ImproveField;
  language: string;
  tone?: string;
  text: string;
  /** Extra factual context the model may use but must not go beyond. */
  context?: Record<string, string | number | boolean>;
}

export const IMPROVE_LIMITS: Record<ImproveField, { maxChars: number; maxOutputTokens: number }> = {
  title: { maxChars: 90, maxOutputTokens: 300 },
  description: { maxChars: 1200, maxOutputTokens: 1200 },
};

const CONTEXT_KEYS = ['title', 'category', 'location', 'venue'] as const;

export function improveSystemPrompt({ field, language, tone }: ImprovePromptInput): string {
  const shared = `You are an expert event marketing copywriter for a public ticketing platform. Respond ONLY in ${language}. ${
    tone ? `Tone: ${tone}. ` : ''
  }Never invent dates, prices, performers, sponsors, amenities or features. Keep every fact the user wrote, including proper nouns.`;

  if (field === 'title') {
    return `${shared} Rewrite the event title so it is clearer and more compelling. Under ${IMPROVE_LIMITS.title.maxChars} characters, no ALL CAPS, no emojis, no quotes, no ending punctuation.`;
  }
  return `${shared} Rewrite the event description so it is clearer and more appealing. If the input is short, expand it tastefully into 2-3 short paragraphs (280-${IMPROVE_LIMITS.description.maxChars} characters). Plain text only — no Markdown, HTML, emojis, hashtags or bullet symbols.`;
}

export function improveUserPrompt({ field, text, context }: ImprovePromptInput): string {
  const contextLines = CONTEXT_KEYS.map((k) => (context?.[k] ? `${k}: ${context[k]}` : ''))
    .filter(Boolean)
    .join('\n');

  return `Improve this event ${field}:\n"""${text}"""\n\n${
    contextLines ? `Helpful context (do not invent beyond this):\n${contextLines}` : ''
  }\n\nRespond with a JSON object: {"improved": string, "rationale": string}.`;
}
